// Timeline (progress-views design §6), drawn in plain SVG.
//
// Summary: the steps AT No game help / Steady / In new places, stacked per
// week — the stack ORDER is fixed and named in the legend (with each band's
// count for the chosen week), so the bands read without colour. Rows: one per
// skill, grouped by area; the pale band is steps started (0–4) and the darker,
// outlined band is steps with no game help. Dotted vertical lines are the
// 13-week review points. Weeks without play are flat; nothing marks a gap.
//
// Tap or hover a row to choose a week; the panel under the rows lists every
// step's stage that week and when it was last played. Labels live in HTML (not
// in the stretched SVG), so the chart works at 360 px.

import { useMemo, useState } from "react";
import { fill, type Locale, type PortalDictionary } from "../../i18n/dictionary";
import { buildTimeline, TIMELINE_RANGES, type TimelineRange } from "../../progress/timeline";
import type { SkillStepState } from "../../domain/types";
import { STEP_COUNT } from "../../progress/skillRegistry.generated";
import { bedLabel, formatDay, lastPractisedLine, stageLabel } from "../../progress/text";
import { SectionTitle } from "./OnePagerView";

type P = PortalDictionary["service"]["progress"];

const RANGE_LABEL: Record<TimelineRange, keyof P> = {
  "3m": "range3m",
  "6m": "range6m",
  "1y": "range1y",
  all: "rangeAll",
};

/** A stepped path over `values` (one per week), filled down to the baseline. */
function steppedArea(values: number[], max: number, h: number, base: number[] = []): string {
  const y = (v: number) => h - (v / max) * h;
  const n = values.length;
  let d = `M0 ${y(base[0] ?? 0)}`;
  for (let i = 0; i < n; i++) d += ` L${i} ${y(values[i])} L${i + 1} ${y(values[i])}`;
  for (let i = n - 1; i >= 0; i--) d += ` L${i + 1} ${y(base[i] ?? 0)} L${i} ${y(base[i] ?? 0)}`;
  return `${d} Z`;
}

function steppedLine(values: number[], max: number, h: number): string {
  const y = (v: number) => h - (v / max) * h;
  let d = `M0 ${y(values[0] ?? 0)}`;
  values.forEach((v, i) => {
    d += ` L${i} ${y(v)} L${i + 1} ${y(v)}`;
  });
  return d;
}

function weekFromPointer(e: React.PointerEvent<SVGSVGElement>, weeks: number): number {
  const r = e.currentTarget.getBoundingClientRect();
  if (r.width <= 0) return weeks - 1;
  return Math.max(0, Math.min(weeks - 1, Math.floor(((e.clientX - r.left) / r.width) * weeks)));
}

export function TimelineView({
  stepStates,
  locale,
  dict,
  now,
}: {
  stepStates: readonly SkillStepState[];
  locale: Locale;
  dict: PortalDictionary;
  now?: Date;
}) {
  const p = dict.service.progress;
  const [range, setRange] = useState<TimelineRange>("3m");
  const model = useMemo(
    () => buildTimeline({ stepStates, range, now, locale }),
    [stepStates, range, now, locale],
  );
  const W = model.weeks.length;
  const [weekIdx, setWeekIdx] = useState<number | null>(null);
  const [skillId, setSkillId] = useState<string | null>(null);
  const wi = weekIdx === null || weekIdx >= W ? W - 1 : weekIdx;
  const selected = model.skills.find((s) => s.skillId === skillId) ?? null;

  if (!model.hasData) return null;

  const sum = model.summary;
  const totals = model.weeks.map((_, i) => sum.own[i] + sum.steady[i] + sum.places[i]);
  const maxT = Math.max(4, ...totals);
  const H = 100;
  const ownTop = sum.own;
  const steadyTop = sum.own.map((v, i) => v + sum.steady[i]);
  const placesTop = steadyTop.map((v, i) => v + sum.places[i]);
  const reviewIdx = model.reviews.map((r) => model.weeks.indexOf(r)).filter((i) => i >= 0);
  const weekLabel = formatDay(locale, model.weeks[wi]);

  const groups: { domain: string; skills: typeof model.skills }[] = [];
  for (const s of model.skills) {
    const g = groups.find((x) => x.domain === s.domain);
    if (g) g.skills.push(s);
    else groups.push({ domain: s.domain, skills: [s] });
  }

  const summaryLabel = fill(p.summaryAria, {
    date: weekLabel,
    own: sum.own[wi],
    steady: sum.steady[wi],
    places: sum.places[wi],
  });

  return (
    <section aria-labelledby="timeline-title" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionTitle id="timeline-title">{p.timelineTitle}</SectionTitle>

      <div role="group" aria-label={p.rangeLabel} style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {TIMELINE_RANGES.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={range === r}
            onClick={() => {
              setRange(r);
              setWeekIdx(null);
            }}
            style={{
              minHeight: 44,
              padding: "0 16px",
              borderRadius: "var(--r-pill)",
              border: `1.5px solid ${range === r ? "var(--brand)" : "var(--line)"}`,
              background: range === r ? "var(--brand-tint)" : "var(--surface)",
              color: range === r ? "var(--brand)" : "var(--ink-soft)",
              fontFamily: "var(--font-body)",
              fontWeight: range === r ? 700 : 600,
              fontSize: 14,
              cursor: "pointer",
              textDecoration: range === r ? "underline" : "none",
            }}
          >
            {p[RANGE_LABEL[r]] as string}
          </button>
        ))}
      </div>

      {/* Summary: steps at own / steady / places, stacked. */}
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "14px 16px",
        }}
      >
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 8px" }}>
          {p.summaryTitle}
        </h3>
        <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
          <div
            aria-hidden="true"
            style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: 11, color: "var(--ink-faint)" }}
          >
            <span>{maxT}</span>
            <span>0</span>
          </div>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            height="120"
            preserveAspectRatio="none"
            role="img"
            aria-label={summaryLabel}
            onPointerMove={(e) => setWeekIdx(weekFromPointer(e, W))}
            onPointerDown={(e) => setWeekIdx(weekFromPointer(e, W))}
            style={{ flex: 1, minWidth: 0, touchAction: "pan-y" }}
          >
            <title>{summaryLabel}</title>
            <line x1="0" x2={W} y1={H} y2={H} stroke="var(--line-strong)" vectorEffect="non-scaling-stroke" />
            <path d={steppedArea(placesTop, maxT, H, steadyTop)} fill="var(--brand)" stroke="var(--surface)" vectorEffect="non-scaling-stroke" />
            <path d={steppedArea(steadyTop, maxT, H, ownTop)} fill="var(--brand)" fillOpacity="0.6" stroke="var(--surface)" vectorEffect="non-scaling-stroke" />
            <path d={steppedArea(ownTop, maxT, H)} fill="var(--brand)" fillOpacity="0.3" stroke="var(--surface)" vectorEffect="non-scaling-stroke" />
            {reviewIdx.map((i) => (
              <line key={i} x1={i} x2={i} y1="0" y2={H} stroke="var(--ink-soft)" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
            ))}
            <line x1={wi + 0.5} x2={wi + 0.5} y1="0" y2={H} stroke="var(--ink-deep)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--ink-faint)", marginTop: 4, paddingLeft: 18 }}>
          <span>{formatDay(locale, model.weeks[0])}</span>
          <span>{formatDay(locale, model.weeks[W - 1])}</span>
        </div>
        {/* Legend, top of the stack first; each band also carries its count for the chosen week. */}
        <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 13, color: "var(--ink-soft)" }}>
          <li>
            <span aria-hidden="true" style={{ display: "inline-block", width: 12, height: 12, background: "var(--brand)", marginRight: 6, verticalAlign: -1 }} />
            {stageLabel(p, "places")}: {sum.places[wi]}
          </li>
          <li>
            <span aria-hidden="true" style={{ display: "inline-block", width: 12, height: 12, background: "var(--brand)", opacity: 0.6, marginRight: 6, verticalAlign: -1 }} />
            {stageLabel(p, "steady")}: {sum.steady[wi]}
          </li>
          <li>
            <span aria-hidden="true" style={{ display: "inline-block", width: 12, height: 12, background: "var(--brand)", opacity: 0.3, marginRight: 6, verticalAlign: -1 }} />
            {stageLabel(p, "own")}: {sum.own[wi]}
          </li>
          <li style={{ color: "var(--ink-faint)" }}>{fill(p.weekOf, { date: weekLabel })}</li>
        </ul>
      </div>

      {/* Skill rows, grouped by area. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {groups.map((g) => (
          <div key={g.domain}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 6px" }}>
              {bedLabel(p, g.domain)}
            </h3>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {g.skills.map((s) => {
                const label = fill(p.rowAria, { skill: s.label, date: weekLabel, started: s.started[wi], own: s.own[wi] });
                const isSel = selected?.skillId === s.skillId;
                return (
                  <li key={s.skillId}>
                    <button
                      type="button"
                      aria-pressed={isSel}
                      aria-label={label}
                      onClick={() => setSkillId(isSel ? null : s.skillId)}
                      style={{
                        width: "100%",
                        minHeight: 44,
                        display: "block",
                        textAlign: "left",
                        padding: "6px 10px",
                        borderRadius: "var(--r-md)",
                        border: `1.5px solid ${isSel ? "var(--brand)" : "var(--line)"}`,
                        background: "var(--surface)",
                        cursor: "pointer",
                        fontFamily: "var(--font-body)",
                      }}
                    >
                      <span style={{ display: "block", fontSize: 13.5, color: "var(--ink)" }}>{s.label}</span>
                      <svg
                        viewBox={`0 0 ${W} 20`}
                        width="100%"
                        height="22"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                        onPointerMove={(e) => setWeekIdx(weekFromPointer(e, W))}
                        onPointerDown={(e) => setWeekIdx(weekFromPointer(e, W))}
                        style={{ display: "block", marginTop: 4, touchAction: "pan-y" }}
                      >
                        <line x1="0" x2={W} y1="20" y2="20" stroke="var(--line)" vectorEffect="non-scaling-stroke" />
                        <path d={steppedArea(s.started, STEP_COUNT, 20)} fill="var(--brand-tint)" />
                        <path d={steppedArea(s.own, STEP_COUNT, 20)} fill="var(--brand)" fillOpacity="0.75" />
                        <path d={steppedLine(s.own, STEP_COUNT, 20)} fill="none" stroke="var(--ink-deep)" strokeWidth="1.25" vectorEffect="non-scaling-stroke" />
                        {reviewIdx.map((i) => (
                          <line key={i} x1={i} x2={i} y1="0" y2="20" stroke="var(--ink-soft)" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
                        ))}
                        <line x1={wi + 0.5} x2={wi + 0.5} y1="0" y2="20" stroke="var(--ink-deep)" vectorEffect="non-scaling-stroke" />
                      </svg>
                      <span aria-hidden="true" style={{ display: "block", marginTop: 2, fontSize: 12, color: "var(--ink-faint)" }}>
                        {fill(p.rowCounts, { started: s.started[wi], own: s.own[wi] })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {selected && (
        <div aria-live="polite" style={{ background: "var(--surface-sunk)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", padding: "12px 16px" }}>
          <b style={{ display: "block", fontSize: 14.5, color: "var(--ink-deep)" }}>{selected.label}</b>
          <span style={{ display: "block", fontSize: 12.5, color: "var(--ink-faint)", marginBottom: 6 }}>
            {fill(p.weekOf, { date: weekLabel })}
          </span>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
            {selected.steps.map((st) => (
              <li key={st.n} style={{ fontSize: 13.5, color: "var(--ink)" }}>
                {fill(p.stepStage, { n: st.n, stage: stageLabel(p, st.stages[wi]) })}
                <span style={{ color: "var(--ink-faint)" }}> · {lastPractisedLine(p, locale, st.lastPractisedOn)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p style={{ margin: 0, fontSize: 12.5, color: "var(--ink-faint)", lineHeight: 1.5 }}>{p.timelineHint}</p>
      {reviewIdx.length > 0 && (
        <ul style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", margin: 0, padding: 0 }}>
          {reviewIdx.map((i) => (
            <li key={i}>{fill(p.reviewMarker, { date: formatDay(locale, model.weeks[i]) })}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
